"use client"
import Link from "next/link"
import Image from "next/image"
import { useEffect, useState, useRef } from "react"
import Icon from "@/components/ui/Icon"
import { useFavourites } from "@/lib/hooks/useFavourites"
import { useDataSaver } from "@/lib/hooks/useDataSaver"
import { sellerDisplayName } from "@/lib/accounts/display-name"
import SellerLink from "@/components/SellerLink"
import { MAX_TRUST_SCORE } from "@/lib/trust/constants"

interface Listing {
  id: string
  title: string
  price: number
  priceType: string
  suburb: string
  city: string
  beds: number
  baths: number
  floorArea: number
  propertyType: string
  status: string
  isFeatured: boolean
  photos: { url: string; isPrimary: boolean }[]
  trustScore: { totalScore: number } | null
  agent: { id: string; name: string; image: string | null; phone: string | null }
  features: { feature: string }[]
}

const CITY_SUBURBS: Record<string, string[]> = {
  "Harare": ["Borrowdale", "Avondale", "Highlands", "Mt Pleasant", "Glen Lorne", "Greendale", "Chisipite", "Newlands", "Eastlea", "Madokero", "Westgate", "Mabelreign", "Northgate", "Hatfield", "Borrowdale Brooke", "Msasa", "Bluffhill", "Kuwadzana", "Budiriro", "Mbare"],
  "Bulawayo": ["Suburbs", "Burnside", "Hillside", "Queens Park", "Pumula", "Nkulumane", "Bellevue", "Famona", "Matsheumhlope", "Waterford", "Woodville", "Selborne Park", "Luveve", "Emganwini", "Makokoba"],
  "Mutare": ["Hobhouse", "Morningside", "Chikanga", "Dangamvhu", "Sakubva", "Greenside", "Palmerstone", "Murambi", "Dangamvhu", "Hobhouse"],
  "Gweru": ["Senga", "Mkoba", "Mambo", "Ascot", "Riverside", "Windsor Park"],
  "Victoria Falls": ["Chinotimba", "Mkhosana", "Manyika"],
  "Masvingo": ["Mucheke", "Rhodene", "Runyararo"],
  "Kwekwe": ["Newtown", "Mbizo", "Amaveni"],
  "Chinhoyi": ["Cold Comfort", "Chikonohono"],
  "Bindura": ["Chiwaridzo", "Chipadze"],
}
const CITIES = ["Harare", "Bulawayo", "Mutare", "Gweru", "Victoria Falls", "Masvingo", "Kwekwe", "Chinhoyi", "Bindura"]

export default function ListingsPage() {
  const [listings, setListings] = useState<Listing[]>([])
  const [loading, setLoading] = useState(true)
  // Separate from `loading`: "Load more" set the shared flag, which swapped the
  // whole results grid for skeletons instead of appending beneath it.
  const [appending, setAppending] = useState(false)
  const [sort, setSort] = useState("newest")
  const { favs, toggle: toggleFavDB } = useFavourites()
  const { dataSaver } = useDataSaver()
  const [compareIds, setCompareIds] = useState<string[]>([])

  const [filters, setFilters] = useState({ q: "", priceType: "", minPrice: "", maxPrice: "", beds: "", baths: "", suburb: "", city: "", propertyType: "", titleDeedVerified: "" })
  const [mustHave, setMustHave] = useState<string[]>([])
  const [suburbSearch, setSuburbSearch] = useState("")
  const [filteredSuburbs, setFilteredSuburbs] = useState<string[]>(Object.values(CITY_SUBURBS).flat())
  const [dynamicSuburbs, setDynamicSuburbs] = useState<string[]>([])
  const [featuredOnly, setFeaturedOnly] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [lastParams, setLastParams] = useState<Record<string, string>>({})
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sessionUser, setSessionUser] = useState<any>(null)
  const [searchSaved, setSearchSaved] = useState(false)
  const [searchSaving, setSearchSaving] = useState(false)

  useEffect(() => {
    setCompareIds(JSON.parse(localStorage.getItem("ze_compare") ?? "[]"))
    const stored = localStorage.getItem("ze_user")
    const expiry = localStorage.getItem("ze_session_expiry")
    if (stored && expiry && new Date(expiry) > new Date()) setSessionUser(JSON.parse(stored))
    const params = new URLSearchParams(window.location.search)
    const isFeatured = params.get("featured") === "true"
    setFeaturedOnly(isFeatured)
    const initial: Record<string, string> = {}
    // titleDeedVerified belongs here too: the diaspora CTA links to
    // /listings?titleDeedVerified=true, and without it the param was dropped
    // and the visitor silently got every listing.
    const filterKeys = ["q", "agentId", "priceType", "suburb", "city", "propertyType", "beds", "baths", "minPrice", "maxPrice", "titleDeedVerified"]
    filterKeys.forEach(k => { if (params.get(k)) initial[k] = params.get(k)! })
    if (isFeatured) initial.featured = "true"
    if (Object.keys(initial).length > 0) {
      setFilters(f => ({ ...f, ...initial }))
      if (initial.suburb) setSuburbSearch(initial.suburb)
      fetchListings(initial)
    } else {
      fetchListings({})
    }
  }, [])

  function toggleFav(id: string, e: React.MouseEvent) { toggleFavDB(id, e) }

  function toggleCompare(id: string, e: React.MouseEvent) {
    e.preventDefault()
    const current: string[] = JSON.parse(localStorage.getItem("ze_compare") ?? "[]")
    const updated = current.includes(id) ? current.filter(f => f !== id) : current.length < 3 ? [...current, id] : current
    localStorage.setItem("ze_compare", JSON.stringify(updated))
    setCompareIds(updated)
  }

  // When a city is selected, fetch real suburbs from live listings (not hardcoded map)
  useEffect(() => {
    if (!filters.city) {
      setDynamicSuburbs([])
      return
    }
    fetch("/api/listings?distinct=suburb&city=" + encodeURIComponent(filters.city))
      .then(r => r.json())
      .then(d => setDynamicSuburbs((d.data ?? []).map((s: { suburb: string }) => s.suburb)))
      .catch(() => setDynamicSuburbs([]))
  }, [filters.city])

  useEffect(() => {
    // Use dynamic API suburbs when a city is selected, otherwise fall back to static map
    const baseSuburbs = filters.city
      ? (dynamicSuburbs.length > 0 ? dynamicSuburbs : (CITY_SUBURBS[filters.city] ?? []))
      : Object.values(CITY_SUBURBS).flat()
    setFilteredSuburbs(suburbSearch
      ? baseSuburbs.filter(s => s.toLowerCase().includes(suburbSearch.toLowerCase()))
      : baseSuburbs)
  }, [suburbSearch, filters.city, dynamicSuburbs])

  // Skip the FIRST run: on mount this fired with the still-empty lastParams and
  // issued a second, unfiltered request that raced the URL-filtered one from the
  // mount effect — whichever landed last won, so ?city=... was silently lost.
  const sortMounted = useRef(false)
  useEffect(() => {
    if (!sortMounted.current) { sortMounted.current = true; return }
    fetchListings(lastParams, 1, false, sort)
  }, [sort])

  // Monotonic id: only the newest in-flight request may write to state, so a
  // slow earlier response can never overwrite newer results.
  const reqSeq = useRef(0)

  function fetchListings(params: Record<string, string>, page = 1, append = false, sortOverride?: string) {
    const seq = ++reqSeq.current
    if (append) setAppending(true); else setLoading(true)
    setLastParams(params)
    setCurrentPage(page)
    const activeSort = sortOverride ?? sort
    const q = new URLSearchParams({ ...params, page: String(page), sort: activeSort }).toString()
    fetch("/api/listings" + (q ? "?" + q : ""))
      .then(r => r.json())
      .then(d => {
        // Drop a stale response: a slower earlier request must not overwrite the
        // results of a newer one the user is actually waiting on.
        if (seq !== reqSeq.current) return
        setListings(prev => append ? [...prev, ...(d.data ?? [])] : (d.data ?? []))
        setTotalPages(d.totalPages ?? 1)
        setLoading(false); setAppending(false)
      })
      .catch(() => { if (seq === reqSeq.current) { setLoading(false); setAppending(false) } })
  }

  function loadMore() {
    fetchListings(lastParams, currentPage + 1, true)
  }

  function buildSearchName(f: typeof filters): string {
    const parts: string[] = []
    if (f.beds) parts.push(f.beds + "+ bed")
    if (f.propertyType) parts.push(f.propertyType.toLowerCase())
    else parts.push("property")
    if (f.suburb) parts.push("in " + f.suburb)
    else if (f.city) parts.push("in " + f.city)
    if (f.priceType) parts.push(f.priceType === "RENT" ? "for rent" : "for sale")
    if (f.minPrice && f.maxPrice) parts.push("$" + f.minPrice + "-$" + f.maxPrice)
    else if (f.maxPrice) parts.push("under $" + f.maxPrice)
    return parts.join(" ") || "All listings"
  }

  async function saveSearch() {
    setSearchSaving(true)
    try {
      const body: Record<string, any> = { name: buildSearchName(filters) }
      if (filters.priceType) body.priceType = filters.priceType
      if (filters.propertyType) body.propertyType = filters.propertyType.toUpperCase()
      if (filters.city) body.city = filters.city
      if (filters.suburb) body.suburb = filters.suburb
      if (filters.minPrice) body.minPrice = Number(filters.minPrice)
      if (filters.maxPrice) body.maxPrice = Number(filters.maxPrice)
      if (filters.beds) body.minBeds = Number(filters.beds)
      if (filters.q) body.query = filters.q
      if (mustHave.length > 0) body.features = mustHave
      const res = await fetch("/api/saved-searches", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      if ((await res.json()).success) setSearchSaved(true)
    } catch {}
    setSearchSaving(false)
  }

  const hasActiveFilters = Object.values(filters).some(v => v !== "")

  // On mobile the filter drawer covered the results, so applying a filter looked
  // like nothing happened: the drawer stayed open and the (updated) results were
  // ~1000px below it. Close it and bring the results into view.
  function closeDrawerAndReveal() {
    if (typeof window === "undefined" || window.innerWidth >= 900) return
    setSidebarOpen(false)
    requestAnimationFrame(() => {
      document.getElementById("ze-results")?.scrollIntoView({ behavior: "smooth", block: "start" })
    })
  }

  function applyFilters(overrides?: Partial<typeof filters>) {
    const f = { ...filters, ...overrides }
    const params: Record<string, string> = {}
    if (f.q) params.q = f.q
    if (f.priceType) params.priceType = f.priceType
    if (f.minPrice) params.minPrice = f.minPrice
    if (f.maxPrice) params.maxPrice = f.maxPrice
    if (f.beds) params.beds = f.beds
    if (f.baths) params.baths = f.baths
    if (f.suburb) params.suburb = f.suburb
    if (f.city) params.city = f.city
    if (f.propertyType) params.propertyType = f.propertyType
    if (f.titleDeedVerified) params.titleDeedVerified = f.titleDeedVerified
    if (mustHave.length > 0) params.features = mustHave.join(",")
    fetchListings(params)
    closeDrawerAndReveal()
  }

  function setFilterAndApply(key: string, val: string) {
    const updated = { ...filters, [key]: val }
    setFilters(updated)
    setSearchSaved(false)
    const params: Record<string, string> = {}
    Object.entries(updated).forEach(([k, v]) => { if (v) params[k] = v })
    // mustHave lives outside `filters`, so it has to be re-attached here or every
    // pill click quietly widens the search while the checkboxes still show ticked.
    if (mustHave.length > 0) params.features = mustHave.join(",")
    fetchListings(params)
  }

  const sorted = listings

  return (
    <div className="listings-layout">
      <aside className={"listings-layout-sidebar" + (sidebarOpen ? "" : " sidebar-hidden-mob")} style={{ borderRight: "1px solid var(--line)", padding: "32px 24px", position: "sticky", top: 68, height: "calc(100vh - 68px)", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 600, color: "var(--navy)" }}>Filters</h3>
          <button onClick={() => { setFilters({ q: "", priceType: "", minPrice: "", maxPrice: "", beds: "", baths: "", suburb: "", city: "", propertyType: "", titleDeedVerified: "" }); setSuburbSearch(""); setMustHave([]); fetchListings({}) }} style={{ fontSize: 12, color: "var(--gold-deep)", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Clear all</button>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Search</div>
          <input value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} onKeyDown={e => { if (e.key === "Enter") applyFilters() }} placeholder="Keyword — e.g. Borrowdale, cluster, borehole" style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>For</div>
          <div style={{ display: "flex", gap: 6 }}>
            {[{ label: "Buy", val: "SALE" }, { label: "Rent", val: "RENT" }].map(t => (
              <button key={t.val} onClick={() => { const newVal = filters.priceType === t.val ? "" : t.val; setFilters(f => ({ ...f, priceType: newVal })); setFilterAndApply("priceType", newVal) }} style={{ padding: "6px 16px", borderRadius: 20, border: "1px solid var(--line)", fontSize: 13, fontWeight: 600, cursor: "pointer", background: filters.priceType === t.val ? "var(--navy)" : "var(--cream)", color: filters.priceType === t.val ? "var(--cream)" : "var(--ink)", fontFamily: "inherit", transition: "all 0.15s" }}>{t.label}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Property type</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {["House", "Apartment", "Cluster", "Land", "Commercial", "Room"].map(t => (
              <button key={t} onClick={() => { const val = filters.propertyType === t ? "" : t; setFilters(f => ({ ...f, propertyType: val })); setFilterAndApply("propertyType", val) }} style={{ padding: "6px 12px", borderRadius: 20, border: "1px solid var(--line)", fontSize: 13, fontWeight: 600, cursor: "pointer", background: filters.propertyType === t ? "var(--navy)" : "var(--cream)", color: filters.propertyType === t ? "var(--cream)" : "var(--ink)", fontFamily: "inherit", transition: "all 0.15s" }}>
                {t === "Room" ? "🛏️ Room to Let" : t}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Bedrooms</div>
          <div style={{ display: "flex", gap: 6 }}>
            {["Any", "1", "2", "3", "4"].map(b => (
              <button key={b} onClick={() => { const val = b === "Any" ? "" : b; setFilters(f => ({ ...f, beds: val })); setFilterAndApply("beds", val) }} style={{ padding: "6px 12px", borderRadius: 20, border: "1px solid var(--line)", fontSize: 13, fontWeight: 600, cursor: "pointer", background: (b === "Any" && !filters.beds) || filters.beds === b ? "var(--navy)" : "var(--cream)", color: (b === "Any" && !filters.beds) || filters.beds === b ? "var(--cream)" : "var(--ink)", fontFamily: "inherit", transition: "all 0.15s" }}>{b === "Any" ? "Any" : b + "+"}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Bathrooms</div>
          <div style={{ display: "flex", gap: 6 }}>
            {["Any", "1", "2", "3"].map(b => (
              <button key={b} onClick={() => { const val = b === "Any" ? "" : b; setFilters(f => ({ ...f, baths: val })); setFilterAndApply("baths", val) }} style={{ padding: "6px 12px", borderRadius: 20, border: "1px solid var(--line)", fontSize: 13, fontWeight: 600, cursor: "pointer", background: (b === "Any" && !filters.baths) || filters.baths === b ? "var(--navy)" : "var(--cream)", color: (b === "Any" && !filters.baths) || filters.baths === b ? "var(--cream)" : "var(--ink)", fontFamily: "inherit", transition: "all 0.15s" }}>{b === "Any" ? "Any" : b + "+"}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Price range (USD)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
            <input value={filters.minPrice} onChange={e => setFilters(f => ({ ...f, minPrice: e.target.value }))} onBlur={() => applyFilters()} placeholder="Min" style={{ padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
            <input value={filters.maxPrice} onChange={e => setFilters(f => ({ ...f, maxPrice: e.target.value }))} onBlur={() => applyFilters()} placeholder="Max" style={{ padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>City</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {CITIES.map(c => (
              <button key={c} onClick={() => { const val = filters.city === c ? "" : c; setFilters(f => ({ ...f, city: val, suburb: "" })); setSuburbSearch(""); setFilterAndApply("city", val) }} style={{ padding: "6px 12px", borderRadius: 20, border: "1px solid var(--line)", fontSize: 12, fontWeight: 600, cursor: "pointer", background: filters.city === c ? "var(--navy)" : "var(--cream)", color: filters.city === c ? "var(--cream)" : "var(--ink)", fontFamily: "inherit", transition: "all 0.15s" }}>{c}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Suburb</div>
          <input value={suburbSearch} onChange={e => setSuburbSearch(e.target.value)} placeholder="Search suburb..." style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", background: "var(--cream)", outline: "none", marginBottom: 8 }} />
          <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid var(--line)", borderRadius: 8, background: "var(--cream)" }}>
            {filteredSuburbs.map(s => (
              <button key={s} onClick={() => { const val = filters.suburb === s ? "" : s; setFilters(f => ({ ...f, suburb: val })); setFilterAndApply("suburb", val) }} style={{ width: "100%", display: "block", padding: "8px 12px", border: "none", borderBottom: "1px solid var(--line)", fontSize: 13, cursor: "pointer", fontFamily: "inherit", background: filters.suburb === s ? "rgba(27,45,84,0.06)" : "transparent", color: filters.suburb === s ? "var(--navy)" : "var(--ink)", textAlign: "left", fontWeight: filters.suburb === s ? 600 : 400, transition: "all 0.15s" }}>{s}{filters.suburb === s ? " ✓" : ""}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Must have</div>
          {["Borehole", "Backup Water", "Solar Power", "Generator", "ZESA", "Pre-paid Meter", "Pool", "Garden", "Electric Fence", "Durawall", "Security Estate", "Tarred Road", "Pet Friendly", "Garage", "Staff Quarters"].map(f => (
            <label key={f} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={mustHave.includes(f)} onChange={() => {
                const newMustHave = mustHave.includes(f) ? mustHave.filter(x => x !== f) : [...mustHave, f]
                setMustHave(newMustHave)
                const params: Record<string, string> = {}
                Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v })
                if (newMustHave.length > 0) params.features = newMustHave.join(",")
                fetchListings(params)
              }} style={{ accentColor: "var(--navy)" }} />
              <span style={{ color: mustHave.includes(f) ? "var(--navy)" : "var(--ink)", fontWeight: mustHave.includes(f) ? 600 : 400 }}>{f}</span>
            </label>
          ))}
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Verification</div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={filters.titleDeedVerified === "true"} onChange={e => setFilterAndApply("titleDeedVerified", e.target.checked ? "true" : "")} style={{ accentColor: "var(--navy)" }} />
            <span style={{ color: filters.titleDeedVerified === "true" ? "var(--navy)" : "var(--ink)", fontWeight: filters.titleDeedVerified === "true" ? 600 : 400 }}>✓ Title deed verified only</span>
          </label>
        </div>

        <button onClick={() => applyFilters()} style={{ width: "100%", padding: "14px", background: "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Apply filters</button>
      </aside>

      <main style={{ padding: "32px 32px" }} className="mob-pad">
        <button className="mob-filter-btn" onClick={() => setSidebarOpen(o => !o)}>
          {sidebarOpen ? "Hide filters" : "Show filters"}
        </button>
        <div style={{ marginBottom: 20 }}>
          {featuredOnly && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(200,150,62,0.1)", border: "1px solid rgba(200,150,62,0.3)", borderRadius: 8, padding: "6px 14px", marginBottom: 12 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--gold-deep)" }}>★ Featured listings only</span>
              <button onClick={() => { setFeaturedOnly(false); fetchListings({}) }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", fontSize: 12, padding: 0, fontFamily: "inherit" }}>Clear</button>
            </div>
          )}
          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 32, fontWeight: 700, color: "var(--navy)" }}>
            {featuredOnly ? (
              <>Featured <em style={{ color: "var(--gold-deep)", fontStyle: "italic" }}>listings</em></>
            ) : filters.priceType === "RENT" ? (
              <>Properties for <em style={{ color: "var(--gold-deep)", fontStyle: "italic" }}>rent</em></>
            ) : filters.priceType === "SALE" ? (
              <>Properties for <em style={{ color: "var(--gold-deep)", fontStyle: "italic" }}>sale</em></>
            ) : filters.suburb ? (
              <>Properties in <em style={{ color: "var(--gold-deep)", fontStyle: "italic" }}>{filters.suburb}</em></>
            ) : filters.city ? (
              <>Properties in <em style={{ color: "var(--gold-deep)", fontStyle: "italic" }}>{filters.city}</em></>
            ) : (
              <>All Properties in <em style={{ color: "var(--gold-deep)", fontStyle: "italic" }}>Zimbabwe</em></>
            )}
          </h1>
          <p id="ze-results" style={{ color: "var(--muted)", fontSize: 14, marginTop: 4, scrollMarginTop: 80 }}>
            {loading ? "Loading..." : sorted.length + " verified listings · Every listing has a Trust Score"}
          </p>
        </div>
        {compareIds.length > 0 && (
          <div style={{ background: "var(--gold)", borderRadius: 10, padding: "10px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)", flex: 1 }}>{compareIds.length} of 3 selected for comparison</span>
            <Link href="/compare" style={{ padding: "7px 16px", background: "var(--navy)", color: "var(--cream)", borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: "none" }}>Compare now</Link>
            <button onClick={() => { localStorage.setItem("ze_compare", "[]"); setCompareIds([]) }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--navy)", fontSize: 13, fontWeight: 600, padding: 0 }}>Clear</button>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <select aria-label="Sort listings" value={sort} onChange={e => setSort(e.target.value)} style={{ padding: "8px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", background: "var(--cream)", outline: "none", cursor: "pointer", minWidth: 0, maxWidth: "100%" }}>
            <option value="newest">Sort: Newest</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="trust">Trust Score</option>
          </select>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {hasActiveFilters && (
              sessionUser ? (
                <button
                  disabled={searchSaving || searchSaved}
                  onClick={saveSearch}
                  style={{ padding: "8px 16px", border: "1px solid " + (searchSaved ? "var(--green)" : "var(--navy)"), borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: searchSaved ? "default" : searchSaving ? "not-allowed" : "pointer", background: searchSaved ? "rgba(61,139,95,0.1)" : "var(--cream)", color: searchSaved ? "var(--green)" : "var(--navy)", fontFamily: "inherit", transition: "all 0.2s" }}
                >{searchSaved ? "Saved!" : searchSaving ? "Saving..." : "Save search"}</button>
              ) : (
                <Link href="/auth/login?next=/listings" style={{ padding: "8px 16px", border: "1px solid var(--navy)", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "var(--navy)", display: "inline-flex", alignItems: "center", transition: "all 0.15s" }}>Save search</Link>
              )
            )}
            <Link href="/map" style={{ padding: "8px 16px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "var(--navy)", display: "inline-flex", alignItems: "center", gap: 4, transition: "all 0.15s" }}>Map</Link>
            <Link href="/compare" style={{ padding: "8px 16px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "var(--navy)", display: "inline-flex", alignItems: "center", gap: 4, transition: "all 0.15s" }}>Compare</Link>
          </div>
        </div>

        {loading ? (
          <div className="listings-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={{ background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 16, height: 380, opacity: 0.5 }}></div>
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 24px" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🏡</div>
            <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, color: "var(--navy)", marginBottom: 10 }}>
              {filters.suburb ? `No listings in ${filters.suburb}` : filters.city ? `No listings in ${filters.city}` : "No listings found"}
            </h2>
            <p style={{ color: "var(--muted)", fontSize: 15, maxWidth: 420, margin: "0 auto 8px", lineHeight: 1.6 }}>
              {filters.suburb
                ? `We don't have any active listings in ${filters.suburb} right now. Be the first to list here, or broaden your search.`
                : filters.city
                ? `No listings match your filters in ${filters.city}. Try adjusting price range, beds, or property type.`
                : "No listings match your current filters. Try widening your search criteria."}
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginTop: 28 }}>
              <button onClick={() => { setFilters({ q: "", priceType: "", minPrice: "", maxPrice: "", beds: "", baths: "", suburb: "", city: "", propertyType: "", titleDeedVerified: "" }); setSuburbSearch(""); setMustHave([]); fetchListings({}) }} className="btn-navy">Clear filters</button>
              <Link href="/list-property" style={{ padding: "12px 22px", background: "transparent", border: "1px solid var(--navy)", borderRadius: 10, fontSize: 14, fontWeight: 700, color: "var(--navy)", textDecoration: "none" }}>List a property here</Link>
            </div>
          </div>
        ) : (
          <div className="listings-grid">
            {sorted.map(listing => {
              const photo = listing.photos?.[0]?.url
              const fallbackBg = listing.priceType === "RENT" ? "linear-gradient(135deg,#3d8b5f,#1B2D54)" : "linear-gradient(135deg,#3a4f7a,#1B2D54)"
              return (
              <Link key={listing.id} href={"/listings/" + listing.id} className="listing-card">
                <div className="listing-img" style={{ background: photo ? undefined : fallbackBg }}>
                  {photo && <Image src={photo} alt={listing.title} fill quality={dataSaver ? 45 : 75} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" style={{ objectFit: "cover" }} />}
                  {listing.isFeatured
                    ? <span className="listing-tag gold">FEATURED</span>
                    : <span className="listing-tag" style={{ background: listing.priceType === "RENT" ? "var(--green)" : "var(--navy)" }}>{listing.priceType === "SALE" ? "FOR SALE" : "FOR RENT"}</span>
                  }
                  <div className="listing-actions">
                    {/* Public browse deliberately omits agent.phone (anti-scraping),
                        so this card could never build a wa.me URL and fell through
                        to the contact form on EVERY public card. The counted
                        redirect resolves the seller's number server-side, so the
                        button now actually reaches them — and the click lands in
                        the seller's stats, which a raw wa.me link never did. */}
                    <a className="listing-action-btn" href={"/api/go/whatsapp?listing=" + encodeURIComponent(listing.id)} target="_blank" rel="noopener noreferrer" aria-label={"Contact the seller about " + listing.title + " on WhatsApp"} onClick={e => e.stopPropagation()}><Icon name="Message" size={15} /></a>
                    <button className="listing-action-btn" onClick={e => toggleCompare(listing.id, e)} title={compareIds.includes(listing.id) ? "Remove from compare" : "Add to compare"} style={{ color: compareIds.includes(listing.id) ? "var(--gold-deep)" : "inherit", fontSize: 13, fontWeight: 700 }}>{compareIds.includes(listing.id) ? "=" : "+"}</button>
                    <button className="listing-action-btn" onClick={e => toggleFav(listing.id, e)} style={{ color: favs.includes(listing.id) ? "var(--red)" : "inherit" }}>{favs.includes(listing.id) ? "♥" : "♡"}</button>
                  </div>
                </div>
                <div className="listing-body">
                  <div className="listing-price">${listing.price.toLocaleString()} <em>{listing.priceType === "RENT" ? "/mo" : "USD"}</em></div>
                  <div className="listing-title">{listing.title}</div>
                  <div className="listing-loc">{listing.suburb}, {listing.city}</div>
                  <div className="listing-meta">
                    {listing.beds > 0 && <span>{listing.beds} beds</span>}
                    {listing.baths > 0 && <span>{listing.baths} bath</span>}
                    {listing.floorArea > 0 && <span>{listing.floorArea}m²</span>}
                  </div>
                  {listing.trustScore && <div className="trust-score">Trust Score {listing.trustScore.totalScore}/{MAX_TRUST_SCORE}</div>}
                  <div className="listing-agent">
                    <div className="agent-avatar">{sellerDisplayName(listing.agent, "ZE").substring(0, 2).toUpperCase()}</div>
                    <div>
                      {/* `nested`: this card is itself a <Link>, and an <a> inside
                          an <a> is invalid HTML that browsers silently un-nest. */}
                      <SellerLink agent={listing.agent} nested className="agent-name" style={{ textDecoration: "underline", textDecorationColor: "var(--line)", textUnderlineOffset: 3 }}>{sellerDisplayName(listing.agent, "Zim Estates")}</SellerLink>
                      <div className="agent-badge">✓ Verified</div>
                    </div>
                  </div>
                </div>
              </Link>
            )
            })}
          </div>
        )}
        {!loading && currentPage < totalPages && (
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <button onClick={loadMore} disabled={appending} style={{ padding: "14px 40px", background: appending ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: appending ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {appending ? "Loading…" : "Load more"}
            </button>
            <div style={{ marginTop: 10, fontSize: 12, color: "var(--muted)" }}>Page {currentPage} of {totalPages}</div>
          </div>
        )}
      </main>
    </div>
  )
}